import { useRef } from 'react'

import { Modal, Select, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import Button from 'src/components/Buttons/Button/Button'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_EMPLOYEE,
  GET_EMPLOYEES,
  GET_EMPLOYEES_ONLY,
} from 'src/graphql/employees.graphql'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import { unformatPhoneNumber } from 'src/lib/phone.utils'

import { createEmployeeSchema } from '../employeeForm.schema'

interface AddUserModalProps {
  opened: boolean
  close: () => void
  /** Called with the created user's role so the list can reveal them if the active filter would hide them. */
  onUserAdded?: (roleType: string) => void
}

const AddUserModal = ({ opened, close, onUserAdded }: AddUserModalProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  // The role that was actually submitted. The Role select stays editable while
  // the request is in flight, so reading form.values on completion could report
  // a role the new user doesn't have — and hide them from the list again.
  const submittedRoleType = useRef('RECRUITER')
  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: 'Recruiter',
      roleType: 'RECRUITER',
    },
    validate: zodResolver(createEmployeeSchema),
    validateInputOnBlur: true,
  })

  const [createEmployee, { loading }] = useMutation(CREATE_EMPLOYEE, {
    refetchQueries: [GET_EMPLOYEES, GET_EMPLOYEES_ONLY],
    onCompleted: () => {
      toast('User added successfully!', 'success')
      // Lets the list reveal the new user when the active role filter would
      // otherwise hide them.
      onUserAdded?.(submittedRoleType.current)
      form.reset()
      close()
    },
    onError: () => {
      toast('Unable to add user, please try again.', 'error')
    },
  })

  const onSubmit = (values) => {
    submittedRoleType.current = String(values.roleType)
    createEmployee({
      variables: {
        input: {
          joinedAt: new Date(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          jobTitle: values.jobTitle.trim(),
          roleType: values.roleType,
          status: 'ACTIVE',
          email: values.email.trim(),
          phone: unformatPhoneNumber(values.phone),
          companyId: currentUser.companyId,
        },
      },
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title="Add User"
      classNames={{
        title: 'font-medium',
      }}
      size="40%"
    >
      <form onSubmit={form.onSubmit(onSubmit)} className="flex flex-col gap-4 ">
        <div className="flex flex-col gap-6">
          <div className="flex flex-row justify-between gap-2 ">
            <TextInput
              label="First Name"
              placeholder="First Name"
              className="w-1/2"
              withAsterisk
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label="Last Name"
              placeholder="Last Name"
              className="w-1/2"
              withAsterisk
              {...form.getInputProps('lastName')}
            />
          </div>

          <TextInput
            label="Email"
            placeholder="Email"
            withAsterisk
            {...form.getInputProps('email')}
          />
          <TextInput
            label="Job Title"
            placeholder="Job Title"
            withAsterisk
            {...form.getInputProps('jobTitle')}
          />
          {isAdmin && (
            <Select
              label="Role"
              placeholder="Select role"
              withAsterisk
              data={[
                { value: 'RECRUITER', label: 'Recruiter' },
                { value: 'OPS', label: 'Ops' },
                { value: 'ADMIN', label: 'Admin' },
              ]}
              {...form.getInputProps('roleType')}
            />
          )}
          <PhoneInput
            label="Phone Number"
            withAsterisk
            placeholder="Phone Number"
            value={form.values.phone}
            onChange={(val) => form.setFieldValue('phone', val)}
            error={form.errors.phone}
          />
        </div>
        <div className="flex flex-row gap-3 border-t pt-4">
          <Button
            text="Cancel"
            variant="outline"
            className="flex-1"
            onClick={close}
          />
          <Button
            text="Save"
            className="flex-1"
            type="submit"
            loading={loading}
            disabled={!form.isValid()}
          />
        </div>
      </form>
    </Modal>
  )
}

export default AddUserModal
